// src/components/export/ExportButton.js - Export button component
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { useNotification } from '../../hooks/useNotification';
import { analyticsEvents } from '../../utils/analyticsEvents';

const ExportButton = ({ type = 'reports', filters = {}, period = 'weekly' }) => {
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const { i18n } = useTranslation();
  const { showSuccess, showError } = useNotification();

  const handleExport = async () => {
    setExporting(true);
    try {
      let url = '';
      let filename = '';

      if (type === 'reports') {
        // Export reports as CSV
        const params = new URLSearchParams();
        if (filters.status) params.append('status', filters.status);
        if (filters.category) params.append('category', filters.category);
        if (filters.violation_type) params.append('violation_type', filters.violation_type);
        if (filters.q) params.append('q', filters.q);
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
        
        url = `/api/export/reports/csv?${params.toString()}`;
        filename = `reports_${new Date().toISOString().split('T')[0]}.csv`;
      } else if (type === 'analytics') {
        const pdfParams = new URLSearchParams({ period });
        const lang = (i18n.language || 'en').split('-')[0];
        if (lang) pdfParams.set('lang', lang);
        url = `/api/export/analytics/pdf?${pdfParams.toString()}`;
        filename = `analytics_${period}_${new Date().toISOString().split('T')[0]}.pdf`;
      }

      const response = await api.get(url, {
        responseType: 'blob',
      });

      // Analytics PDF may return JSON with downloadUrl when S3 is used
      if (type === 'analytics') {
        const contentType = response.headers['content-type'] || '';
        if (contentType.includes('application/json')) {
          const text = await response.data.text();
          const data = JSON.parse(text);
          if (data.downloadUrl) {
            // Open presigned S3 URL in new tab to avoid CORS (navigation, not fetch)
            window.open(data.downloadUrl, '_blank', 'noopener,noreferrer');
            showSuccess('Analytics exported successfully');
            return;
          }
        }
      }

      // Create download link from blob (CSV or direct PDF)
      const blob = new Blob([response.data], {
        type: type === 'reports' ? 'text/csv' : 'application/pdf'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      showSuccess(`${type === 'reports' ? 'Reports' : 'Analytics'} exported successfully`);
      if (type === 'analytics') {
        analyticsEvents.exportAnalyticsPdf(period);
      }
    } catch (error) {
      console.error('Export error:', error);
      let message = error.message;
      if (error.response?.data) {
        const data = error.response.data;
        if (data instanceof Blob) {
          try {
            const text = await data.text();
            const json = JSON.parse(text);
            message = json.message || json.error || json.details || message;
          } catch (_) {
            if (error.response?.status >= 400) {
              message = type === 'analytics' ? 'PDF export failed (server error)' : 'Export failed (server error)';
            }
          }
        } else if (typeof data === 'object' && data.error) {
          message = data.error;
        }
      }
      showError(`Failed to export ${type}: ${message}`);
    } finally {
      setExporting(false);
    }
  };

  if (type === 'analytics') {
    // Analytics only supports PDF
    return (
      <button
        onClick={handleExport}
        disabled={exporting}
        className="px-4 py-2 text-sm text-white bg-shayek-primary dark:bg-shayek-primary-600 rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors shadow-shayek"
      >
        {exporting ? (
          <>
            <span className="animate-spin">⏳</span>
            Exporting...
          </>
        ) : (
          <>
            📄 Export PDF
          </>
        )}
      </button>
    );
  }

  // Reports support CSV
  return (
    <div className="flex items-center gap-2">
      <select
        value={exportFormat}
        onChange={(e) => setExportFormat(e.target.value)}
        className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
        disabled={exporting}
      >
        <option value="csv">CSV</option>
      </select>
      <button
        onClick={handleExport}
        disabled={exporting}
        className="px-4 py-2 text-sm text-white bg-shayek-primary dark:bg-shayek-primary-600 rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors shadow-shayek"
      >
        {exporting ? (
          <>
            <span className="animate-spin">⏳</span>
            Exporting...
          </>
        ) : (
          <>
            📥 Export {exportFormat.toUpperCase()}
          </>
        )}
      </button>
    </div>
  );
};

export default ExportButton;

